export type Entity = {
  id: string
  type: string
  timestamp?: number
}

export type Etag = Entity & {
  type: 'Etag'
  entityType: string
  timestamp: number
}

export type EntityDict<T extends { id: string }> = { [id: string]: T }

export const domainsOfExcellence = {
  InfectionPrevention: 'Infection prevention',
  PatientExperienceAndSatisfaction: 'Patient experience and satisfaction',
  PatientSafety: 'Patient safety',
  EfficientResourceUtilization: 'Efficient resource utilization',
  MedicationSafety: 'Medication safety',
  SafeSurgery: 'Safe surgery'
} as const

export type DomainOfExcellence = keyof typeof domainsOfExcellence

export const inviteStatuses = {
  Invited: 'Invited',
  Accepted: 'Accepted',
  Revoked: 'Revoked',
  Expired: 'Expired'
} as const

export type InviteStatus = keyof typeof inviteStatuses

export type UserOrganizationDetail = {
  organizationId: string
  inviteStatus?: InviteStatus
  permissionGroupId?: string | null
}

export type UserSlim = Entity & {
  email?: string
  displayName?: string
  color: string
  type: 'UserSlim'
  userId: string
  search: string
}

export function userSlimId(userId: string) {
  return `UserSlim_${userId}`
}

export function toUserSlim(user: Pick<User, 'id' | 'email' | 'displayName' | 'color'>): UserSlim {
  return {
    id: userSlimId(user.id),
    type: 'UserSlim',
    userId: user.id,
    email: user.email,
    displayName: user.displayName,
    color: user.color,
    search: `${user.displayName}|${user.email}`.toLocaleLowerCase()
  }
}

export type UserListItem = UserSlim & {
  lastLoginDate?: string
  userOrganizationDetails: UserOrganizationDetail[]
}

export type User = Entity & {
  email: string
  displayName?: string
  color: string
  language: string
  role: string
  country: string
  isOsso: boolean
  hasRunTutorial: boolean
  hasRunCollabTutorial: boolean
  healthStreamId?: string
  permissionGroupIds?: string[]
  organizationIds: string[]
  organizationPermissions?: OrganizationPermissionGroup[]
  type: 'User'
}

export type OrganizationPermissionGroup = {
  organizationId: string
  permissionGroupIds: string[]
}

export const buildPlatforms = ['linux', 'quest', 'windows'] as const

export type BuildPlatform = (typeof buildPlatforms)[number]

export type Platform = 'web' | BuildPlatform

export type Course = Entity & {
  name: string
  buildId: string
  techniqueId: string
  type: 'course'
  healthStreamCourseId?: string
}

export type Stage = {
  id?: string
  criticalActions: CriticalAction[]
  name: string
}

export type CriticalAction = {
  id: string
  description: string
  criticalSubActions?: CriticalSubAction[]
  domainsOfExcellence?: DomainOfExcellence[]
}

export type CriticalSubAction = {
  id: string
  description: string
  supplySelectionItems?: SupplySelectionItem[]
}

export type SupplySelectionItem = {
  id: string
  name: string
  correct: boolean
}

export type RunPerformance = {
  id: string
  date: string
  score: number
  isTest: boolean
  criticalActions: CriticalActionEvent[]
}

export type CriticalActionEvent = { id: string; passed: boolean }

export type PlayerHintEvent = { id: string; criticalAction?: string }

export type SupplySelectionEvent = {
  id: string
  itemsPlaced: string[]
}

export type CriticalSubActionEvent = {
  id: string
  passed: boolean
  parent: string
}

export type PlayerRun = {
  id: string
  userId?: string
  date?: string
  techniqueId?: string
  playerBuildId?: string
  criticalActions: CriticalActionEvent[]
  hints: PlayerHintEvent[]
  supplySelections: SupplySelectionEvent[]
  criticalSubActions: CriticalSubActionEvent[]
  score: number
  baseScore: number
  passed: boolean
  isTest: boolean
  completed: boolean
  time: number
  hintsUsed: number
  failedContaminationCriticalActionCount: number
  courseId?: string
  moduleBuildId?: string
  multiplayerSessionId?: string
  exitModule?: boolean
}

export type ResultStatus = 'correct' | 'incorrect' | 'incomplete' | 'extra'

export type CriticalActionDetailSection = {
  title?: string
  items: CriticalActionDetailItem[]
}

export type CriticalActionDetailItem = {
  id: string
  status: ResultStatus
  label: string
  expectedLabel?: string
}

export type CriticalActionResult = {
  criticalAction: CriticalAction
  criticalActionDetails?: CriticalActionDetailSection[]
  passed: boolean
  hints: PlayerHintEvent[]
}

export type StageResult = {
  title: string
  criticalActionResults: CriticalActionResult[]
  hints: PlayerHintEvent[]
  passed: boolean
}

export type RunResult = {
  id: string
  score: number
  baseScore: number
  passed: boolean
  isTest: boolean
  completed: boolean
  time: number
  hintsUsed: number
  failedContaminationCriticalActionCount: number
  courseId?: string
  multiplayerSessionId?: string
  exitModule?: boolean
  stageResults: StageResult[]
  passedCriticalActions: number
  totalCriticalActions: number
  formattedDuration: string
  techniqueId?: string
}

export type UserCoursePerformance = Entity & {
  organizationId: string
  courseId: string
  userId: string
  recentRuns: RunPerformance[]
  bestRun: RunPerformance
  previousBestRun?: RunPerformance
  totalRunCount: number
  courseCompletedDate?: string
  type: 'userCoursePerformance'
}

export type GitInfo = {
  sha: string
  branch: string
  username: string
  repo: string
}

export type EmbeddedModule = {
  techniqueId: string
  techniquePath: string
  gitInfo: GitInfo
  stages: Stage[]
}

export type PlayerBuild = Entity & {
  type: 'PlayerBuild'
  uri: string
  created: string
  md5Hash: string
  size: number
  version: string
  playerGitInfo: GitInfo
  coreVRGitInfo: GitInfo
  ossoSharedGitInfo: GitInfo
  embeddedModules: EmbeddedModule[]
  description: string
  username: string
  packageName: string
  serverBuildId?: string
  clientBuildIdsByPlatform: Record<string, string[]>
  platform: BuildPlatform
  buildTarget: string
}

export type ModuleBuild = Entity & {
  techniqueId: string
  techniquePath: string
  uri: string
  description: string
  date: string
  clientVersion: string
  unityVersion: string
  stages?: Stage[]
  ossoSharedGitInfo?: GitInfo
  moduleGitInfo?: GitInfo
  coreVRGitInfo?: GitInfo
  type: 'ModuleBuild'
  serverBuildId: string
  platform: BuildPlatform
  questModuleBuildId?: string
  windowsModuleBuildId?: string
}

export type ModuleBuildSettings = Entity & {
  type: 'ModuleBuildSettings'
  isArchived: boolean
}

export type PlayerBuildSettings = Entity & {
  type: 'PlayerBuildSettings'
  isArchived: boolean
  isSubmittedToMeta: boolean
  notes?: string
  metaUrl?: string
}

export type ModuleBuildListItem = Entity & {
  type: 'ModuleBuildListItem'
  date: string
  techniquePath: string
  techniqueId: string
  platform: BuildPlatform
  description: string
  clientVersion: string
  isArchived: boolean
}

export type PlayerBuildListItem = Entity & {
  type: 'PlayerBuildListItem'
  created: string
  platform: BuildPlatform
  version: string
  description: string
  username: string
  serverBuildId?: string
  isArchived: boolean
  isSubmittedToMeta: boolean
  notes?: string
  metaUrl?: string
}

export function userCoursePerformanceId(organizationId: string, courseId: string, userId: string) {
  return `UserCoursePerformance_${organizationId}_${courseId}_${userId}`
}

export function arrayToEntityDict<T extends Entity>(array: T[]): EntityDict<T> {
  return array.reduce((acc: EntityDict<T>, x: T) => {
    acc[x?.id] = x
    return acc
  }, {} as EntityDict<T>)
}

export function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}
